// What sits on top of a participant's picture in a camera, speaker or Dynamic grid layer: their name, a
// placeholder while there's no camera picture, and a green ring while they're speaking. Built as one absolutely
// positioned layer inside `parent` (which has to be positioned itself), so the mounters only decide when to
// show which and leave the look to this file.
const SPEAKING_COLOR = '#65f0a3';
// RMS of the audio, sampled every SAMPLE_MS - the same technique and threshold the call page used before the
// studio: an AnalyserNode taps the stream independently of the (muted) <video> that shows it.
const SPEAKING_THRESHOLD = 0.02;
const SAMPLE_MS = 100;

let sharedAudioContext = null;
const getAudioContext = () => sharedAudioContext ||= new AudioContext();

export function createParticipantOverlay(parent) {
	const root = document.createElement('div');
	Object.assign(root.style, {
		position: 'absolute', inset: '0', pointerEvents: 'none', containerType: 'size', overflow: 'hidden',
	});

	// Stands in for the picture while there's none: the person's initial in a circle.
	const avatar = document.createElement('div');
	Object.assign(avatar.style, {
		position: 'absolute', inset: '0', display: 'none', alignItems: 'center', justifyContent: 'center',
		background: 'linear-gradient(135deg, #2b2f3a, #1a1c23)',
	});
	const initial = document.createElement('div');
	Object.assign(initial.style, {
		height: '46cqmin', width: '46cqmin', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
		background: 'rgba(255,255,255,.14)', color: 'rgba(255,255,255,.85)', fontWeight: '600', fontSize: '22cqmin',
		fontFamily: 'sans-serif',
	});
	avatar.appendChild(initial);

	const label = document.createElement('div');
	Object.assign(label.style, {
		position: 'absolute', left: '2cqmin', bottom: '2cqmin', maxWidth: '90%', padding: '.1em .5em', boxSizing: 'border-box',
		borderRadius: '.3em', background: 'rgba(0,0,0,.6)', color: '#fff', fontFamily: 'sans-serif',
		fontSize: 'max(11px, min(6cqmin, 22px))', lineHeight: '1.4', whiteSpace: 'nowrap', overflow: 'hidden',
		textOverflow: 'ellipsis', display: 'none',
	});

	const ring = document.createElement('div');
	Object.assign(ring.style, {
		position: 'absolute', inset: '0', boxSizing: 'border-box', border: '3px solid transparent', borderRadius: '4px',
		transition: 'border-color .1s',
	});

	// A crossed-out microphone, top right, while they're muted.
	const mutedBadge = document.createElement('div');
	Object.assign(mutedBadge.style, {
		position: 'absolute', top: '2cqmin', right: '2cqmin', width: 'max(20px, 8cqmin)', height: 'max(20px, 8cqmin)', borderRadius: '50%',
		background: 'rgba(220,53,69,.9)', display: 'none', alignItems: 'center', justifyContent: 'center',
	});
	mutedBadge.innerHTML = '<svg viewBox="0 0 16 16" width="62%" height="62%" fill="#fff"><path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4.02 4.02 0 0 0 12 8V7a.5.5 0 0 1 1 0v1zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a4.973 4.973 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4zm3-9v4.879L5.158 2.037A3.001 3.001 0 0 1 11 3z"/><path d="M9.486 10.607 5 6.12V8a3 3 0 0 0 4.486 2.607zm-7.84-9.253 12 12 .708-.708-12-12-.708.708z"/></svg>';

	root.append(avatar, label, mutedBadge, ring);
	parent.appendChild(root);

	let speakingTimer = null;
	let speakingNodes = [];

	function stopSpeaking() {
		clearInterval(speakingTimer);
		speakingTimer = null;
		for(const node of speakingNodes) {
			try { node.disconnect(); } catch(e) { /* already gone */ }
		}
		speakingNodes = [];
		ring.style.borderColor = 'transparent';
	}

	return {
		setName(name) {
			label.textContent = name || '';
			label.style.display = name ? 'block' : 'none';
			initial.textContent = (name || '?').trim().charAt(0).toUpperCase() || '?';
		},
		setMuted(muted) {
			mutedBadge.style.display = muted ? 'flex' : 'none';
		},
		setCameraOff(off) {
			avatar.style.display = off ? 'flex' : 'none';
		},
		// Watches this stream's audio for speech; null (or no audio track) just clears the ring.
		setStream(stream) {
			stopSpeaking();
			if(!stream?.getAudioTracks().length) return;
			try {
				const ctx = getAudioContext();
				const source = ctx.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
				const analyser = ctx.createAnalyser();
				analyser.fftSize = 1024;
				// Silent, but a graph that ends nowhere isn't always pulled, so it's routed to the output at zero gain.
				const silence = ctx.createGain();
				silence.gain.value = 0;
				source.connect(analyser);
				analyser.connect(silence);
				silence.connect(ctx.destination);
				speakingNodes = [ source, analyser, silence ];
				const samples = new Float32Array(analyser.fftSize);
				speakingTimer = setInterval(() => {
					analyser.getFloatTimeDomainData(samples);
					let sum = 0;
					for(const v of samples) sum += v * v;
					ring.style.borderColor = Math.sqrt(sum / samples.length) > SPEAKING_THRESHOLD ? SPEAKING_COLOR : 'transparent';
				}, SAMPLE_MS);
			} catch(e) {
				console.error('Speaking detector error', e);
			}
		},
		destroy() {
			stopSpeaking();
			root.remove();
		},
	};
}
